/**
 * InlineImageSelection - Compact 2x2 grid for selecting images within chat.
 *
 * Design: DENiZEN
 * - Smaller scale for inline display
 * - Click to enlarge with lightbox
 * - Soft borders and shadows
 * - Rounded elements with high contrast
 * - WCAG 2.1 AA compliant with keyboard navigation
 */

import { useState } from 'react'
import type { ImageOption } from '@/services/api'
import { ImageLightbox } from './ImageLightbox'

export interface InlineImageSelectionProps {
  /**
   * Array of image options to display (up to 4)
   */
  images: ImageOption[]
  /**
   * Callback when user confirms their selection
   */
  onSelect: (selectedImage: ImageOption) => void
  /**
   * Whether selection is currently being processed
   */
  isLoading?: boolean
  /**
   * Whether images are being generated (shows skeleton placeholders)
   */
  isGenerating?: boolean
}

export function InlineImageSelection({
  images,
  onSelect,
  isLoading = false,
  isGenerating = false,
}: InlineImageSelectionProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [lightboxImage, setLightboxImage] = useState<string | null>(null)

  const handleImageClick = (index: number) => {
    if (!isLoading) {
      setSelectedIndex(index)
    }
  }

  const handleEnlarge = (imageUrl: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setLightboxImage(imageUrl)
  }

  const handleConfirm = () => {
    if (selectedIndex !== null && images[selectedIndex]) {
      onSelect(images[selectedIndex])
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleImageClick(index)
    }
  }

  return (
    <>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-3">
          <h3
            className="text-lg font-display font-bold text-dark-green tracking-tight"
            style={{ letterSpacing: '-0.02em' }}
          >
            {isGenerating ? 'Creating Your Visions' : 'Choose Your Vision'}
          </h3>
          <p className="text-sm text-muted font-body">
            {isGenerating
              ? 'AI is generating images based on your conversation...'
              : 'Click an image to select, or tap the expand icon to enlarge'}
          </p>
        </div>

        {/* 2x2 Grid - Compact */}
        <div
          className="grid grid-cols-2 gap-2 mb-3"
          role={isGenerating ? undefined : 'radiogroup'}
          aria-label={isGenerating ? 'Images being generated' : 'Image selection grid'}
        >
          {isGenerating ? (
            // Skeleton placeholders during generation
            Array.from({ length: 4 }).map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className="relative aspect-square overflow-hidden border border-dark-green/15 bg-stone rounded-2xl shadow-denizen-sm"
              >
                {/* Animated shimmer effect */}
                <div
                  className="absolute inset-0 animate-pulse"
                  style={{
                    background: 'linear-gradient(90deg, #fff8eb 0%, #f0ead8 50%, #fff8eb 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s ease-in-out infinite',
                  }}
                />

                {/* Center icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 border border-dark-green/15 rounded-full flex items-center justify-center bg-white shadow-denizen-sm">
                    <svg
                      className="w-5 h-5 text-muted animate-spin"
                      style={{ animationDuration: '2s' }}
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="3"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </div>
                </div>

                {/* Number badge */}
                <div className="absolute top-2 left-2 w-6 h-6 rounded-full border border-dark-green/15 flex items-center justify-center font-display font-bold text-xs bg-white shadow-denizen-sm">
                  {index + 1}
                </div>
              </div>
            ))
          ) : (
            // Actual images when available
            <>
              {images.map((image, index) => {
                const isSelected = selectedIndex === index

                return (
                  <button
                    key={image.image_id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={`Vision option ${index + 1}`}
                    onClick={() => handleImageClick(index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    disabled={isLoading}
                    className={`
                      relative aspect-square overflow-hidden
                      border border-dark-green/15 rounded-2xl
                      transition-all duration-150
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green
                      ${isLoading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                      ${isSelected ? 'scale-[0.98]' : 'hover:scale-[1.02]'}
                      animate-fade-in
                    `}
                    style={{
                      boxShadow: isSelected
                        ? 'inset 0 0 0 3px var(--color-mid-green, #40aa3b), var(--shadow-denizen-sm)'
                        : 'var(--shadow-denizen-sm)',
                      animationDelay: `${index * 100}ms`,
                    }}
                  >
                    {/* Image */}
                    <img
                      src={image.image_url}
                      alt={`Generated vision option ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />

                    {/* Selection overlay */}
                    {isSelected && (
                      <div
                        className="absolute inset-0 flex items-center justify-center bg-mid-green/20"
                      >
                        <div className="w-10 h-10 border border-dark-green/15 rounded-full flex items-center justify-center bg-mid-green shadow-denizen-sm">
                          <svg
                            className="w-5 h-5 text-dark-green"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      </div>
                    )}

                    {/* Number badge */}
                    <div
                      className="absolute top-2 left-2 w-6 h-6 rounded-full border border-dark-green/15 flex items-center justify-center font-display font-bold text-xs shadow-denizen-sm"
                      style={{
                        backgroundColor: isSelected ? 'var(--color-mid-green, #40aa3b)' : '#FFFFFF',
                      }}
                    >
                      {index + 1}
                    </div>

                    {/* Expand button */}
                    <button
                      type="button"
                      onClick={(e) => handleEnlarge(image.image_url, e)}
                      className="absolute top-2 right-2 w-6 h-6 border border-dark-green/15 bg-white rounded-full flex items-center justify-center hover:bg-light-green transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green shadow-denizen-sm"
                      aria-label={`Enlarge vision ${index + 1}`}
                    >
                      <svg
                        className="w-3 h-3 text-dark-green"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                        />
                      </svg>
                    </button>
                  </button>
                )
              })}

              {/* Empty slots if fewer than 4 images */}
              {Array.from({ length: Math.max(0, 4 - images.length) }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="aspect-square border border-dark-green/15 border-dashed bg-stone rounded-2xl flex items-center justify-center"
                >
                  <div className="text-muted font-body text-xs">
                    N/A
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Generation progress indicator */}
        {isGenerating && (
          <div className="mb-3">
            <div className="flex items-center gap-2 text-sm text-muted font-body">
              <svg
                className="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>This usually takes 30-60 seconds</span>
            </div>
          </div>
        )}

        {/* Confirm Button - Only show when not generating */}
        {!isGenerating && images.length > 0 && (
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectedIndex === null || isLoading}
            className={`
              w-full px-4 py-3 border border-dark-green/15 font-display font-bold text-sm rounded-2xl
              transition-all duration-150
              focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green
              ${
                selectedIndex !== null && !isLoading
                  ? 'cursor-pointer hover:-translate-y-0.5 bg-dark-green text-white shadow-denizen-sm'
                  : 'cursor-not-allowed opacity-50 bg-stone text-muted'
              }
            `}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Selecting...
              </span>
            ) : (
              'Choose This Vision'
            )}
          </button>
        )}
      </div>

      {/* Lightbox */}
      <ImageLightbox
        imageUrl={lightboxImage || ''}
        alt="Enlarged vision"
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
      />
    </>
  )
}

export default InlineImageSelection
